import type { Level } from "./levels";
import type { Logger } from "./renderer";

export const IPC_INTERFACE = {
  SESSION: "logger/session",
  USER_DATA: "logger/user-data",
  COLLECT: "logger/renderer:collect",
  FETCH_REQUEST: "logger/renderer/fetch:request",
  FETCH_RESPONSE: "logger/renderer/fetch:response",
  SET_FILTER: "logger/renderer/filters:set",
  UNSET_FILTER: "logger/renderer/filters:unset",
  GET_FILTERS: "logger/renderer/filters:get",
  RESET_FILTERS: "logger/renderer/filters:reset",
  CLEAR: "logger/renderer:clear",
};

export const IDENTIFIERS = {
  APP: "app",
  DASHBOARD: "dashboard",
  MENUBAR: "menubar",
  CONTROLLER: "controller",
};

export const DEFAULT_MAX_ENTRIES = 2000;
export const DEFAULT_MAX_BYTES = 512_000;
export const DEFAULT_MAX_SERIALIZED_LENGTH = 10_000;
export const DEFAULT_LEVEL: Level = "info";
export const DEFAULT_MODULE = "main";
/**
 * Default module filters, per renderer process: each renderer only enables
 * the modules whose code can actually run in it, so the Logs settings page
 * doesn't show (and the logger doesn't carry) filters for modules that can
 * never log there.
 */
export const DEFAULT_FILTERS: Record<
  "app" | "dashboard" | "menubar",
  Logger.Filters
> = {
  // Main window: recording composables/automation, planner, editor.
  app: {
    [DEFAULT_MODULE]: DEFAULT_LEVEL,
    // TEMPORARY: to improve meeting recording
    "recording/meetingAutomation": "debug",
    useAudioRecording: "debug",
    useAudioTranscript: "debug",
    useMeetingRecording: "debug",
    useAutoMeetingRecording: "debug",
    "settings/meetings": "debug",
    // TEMPORARY: to debug document merge (duplicate-line / character-loss incidents)
    useDocumentMerge: "debug",
  },
  // Dashboard window: hosts the journal editor (TitledDocument/Editor),
  // which carries the document merge logic and the recording toolbar.
  dashboard: {
    [DEFAULT_MODULE]: DEFAULT_LEVEL,
    // TEMPORARY: to improve meeting recording
    useAudioRecording: "debug",
    useAudioTranscript: "debug",
    useMeetingRecording: "debug",
    // TEMPORARY: to debug document merge (duplicate-line / character-loss incidents)
    useDocumentMerge: "debug",
  },
  // Menubar window: hosts the recording pipeline and the meeting audio
  // detector.
  menubar: {
    [DEFAULT_MODULE]: DEFAULT_LEVEL,
    // TEMPORARY: to improve meeting recording
    "recording/core": "debug",
    "recording/host": "debug",
    "recording/processors": "debug",
    "recording/transcript": "debug",
    "recording/audio": "debug",
    useAudioRecording: "debug",
    "menubar/recordingHost": "debug",
    "menubar/meetingAudioDetector": "debug",
  },
};

/**
 * A fresh copy of the default filters for a renderer identifier (falling
 * back to the main window's defaults). Copied so callers can mutate their
 * filter set without corrupting the shared constants.
 */
export function defaultFiltersFor(identifier: string | null): Logger.Filters {
  if (identifier === "dashboard") return { ...DEFAULT_FILTERS.dashboard };
  if (identifier === "menubar") return { ...DEFAULT_FILTERS.menubar };
  return { ...DEFAULT_FILTERS.app };
}
export const DEFAULT_OPTIONS: Logger.Options = {
  defaultMaxSerializedLength: DEFAULT_MAX_SERIALIZED_LENGTH,
  moduleMaxSerializedLength: {
    // Keep editor investigation payloads unconstrained so the next duplicate-line
    // or character-loss incident captures full merge/editor inputs.
    editor: null,
    useDocumentMerge: null,
  },
};
